/**
 * features/timetable/LiveViews.tsx
 * ------------------------------------------------------------------
 * The Timetable screen AT THIS MOMENT (the school, 2026-09-29, from three
 * previews each):
 *
 *   MyDay      a teacher's own day as a COUNTDOWN AGENDA (option C): the
 *              lessons already taught folded into one line, the lesson on now
 *              with a countdown ring, room, co-teachers and the register, then
 *              the rest of the day; beside it the day at a glance, today's exam
 *              duties and what tomorrow starts with.
 *   SchoolNow  the whole school for anyone who may look up any timetable,
 *              opening on EVERY TEACHER RIGHT NOW (option B): status (teaching
 *              / free / exam duty), where, what is next, their day in a strip.
 *              A Classes tab keeps the class board. A day and period picker
 *              looks elsewhere; "Back to now" returns.
 *
 * Every rule is lib/liveTimetable.ts (pure, tested). The clock is Gaborone's.
 * Re-renders every 30 seconds, so the period rolls over on its own.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { CalendarDays, Check, ClipboardCheck, Clock, Coffee, DoorOpen, Search, Users } from 'lucide-react';
import { db } from '@/lib/firebase';
import {
  DAYS, coTeachers, isTeachingSlot, nextTeachingDay, slotGroups,
  type Day, type PointerKey, type ScheduleSettings, type SlotGroup, type SlotId, type TeacherPhase,
  type TimetablePointer, type TimetableSlot,
} from '@/lib/timetable';
import {
  classBoard, dutiesToday, familyOf, freeRoomsAt, hhmm, lessonRoom, lessonTitle, liveMoment,
  schoolClock, staffAt, teacherDay, type CoverWindow, type DayRow, type Family, type StaffStatus, type SubjectNamer,
} from '@/lib/liveTimetable';
import type { ExamPaperDuties } from '@/lib/examDuties';
import { SoftCard, StatChip, StatStrip, Tabs, glassFieldClass } from '@/components/ui/design';
import { coversFor } from '@/lib/cover';
import { useCoversOn, useMyCovers } from '@/features/cover/useCovers';

/* ============================ shared hooks ============================ */

export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** One day's exam duties (reportExamDuties is staff-readable). Empty outside a series. */
export function useDutiesOn(ymd: string | null): ExamPaperDuties[] {
  const [papers, setPapers] = useState<ExamPaperDuties[]>([]);
  useEffect(() => {
    if (!ymd) { setPapers([]); return; }
    let live = true;
    getDocs(query(collection(db, 'reportExamDuties'), where('date', '==', ymd)))
      .then((s) => { if (live) setPapers(s.docs.map((d) => ({ ...(d.data() as ExamPaperDuties), id: d.id }))); })
      .catch(() => { if (live) setPapers([]); });
    return () => { live = false; };
  }, [ymd]);
  return papers;
}

const FAMILY: Record<Family, string> = {
  sci: 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  maths: 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  lang: 'bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200',
  hum: 'bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-200',
  arts: 'bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
  other: 'bg-[var(--sp-track)] text-[var(--sp-body)]',
};
const FAMILY_LABEL: Record<Family, string> = {
  sci: 'Sciences', maths: 'Maths', lang: 'Languages', hum: 'Humanities & business', arts: 'Arts, PE, ICT', other: 'Other',
};

const time = (t: string) => t.replace('-', '–');
const parseHm = (t: string): number | null => { const m = /^(\d{1,2}):(\d{2})/.exec(t); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };

/* ============================ a teacher's day ============================ */

export function MyDay({
  teacherId, slots, schedule, nameOf, subjectOf, onOpenWeek,
}: {
  teacherId: string;
  slots: Record<SlotId, TimetableSlot>;
  schedule: ScheduleSettings | null;
  nameOf: (id: string) => string;
  subjectOf: SubjectNamer;
  onOpenWeek: () => void;
}) {
  const now = useNow();
  const clock = schoolClock(now);
  const moment = liveMoment(schedule, clock);
  const today = clock.day;
  const rows = useMemo(() => (today ? teacherDay(teacherId, slots, schedule, today, clock) : []),
    [teacherId, slots, schedule, today, clock.minutes]); // eslint-disable-line react-hooks/exhaustive-deps
  const duties = dutiesToday(useDutiesOn(today ? clock.ymd : null), teacherId, clock.ymd);
  /* COVER (lib/cover.ts): a cover given to this teacher today is a DUTY on
     their day, drawn in fuchsia in the period it takes. */
  const covers = coversFor(teacherId, clock.ymd, useMyCovers(today ? teacherId : null, today ? clock.ymd : null));
  const coverIn = (r: { from: number; to: number }) => covers.find((c) => c.fromMin < r.to && r.from < c.toMin) ?? null;
  const coverText = (c: (typeof covers)[number]) => `${c.classes.join(', ')} ${c.subject}`.trim();

  const title = (r: DayRow) => lessonTitle(r, subjectOf);
  const lessons = rows.filter((r) => r.kind === 'lesson');
  const done = rows.filter((r) => r.when === 'done');
  const doneLessons = done.filter((r) => r.kind === 'lesson');
  const nowRow = rows.find((r) => r.when === 'now') ?? null;
  const later = rows.filter((r) => r.when === 'later');
  const freesLeft = rows.filter((r) => r.kind === 'free' && r.when !== 'done' && !coverIn(r));
  const lastBell = rows.length ? hhmm(rows[rows.length - 1].to) : '';
  const tomorrow = useMemo(() => {
    // LKC: the next TEACHING day's cycle day, not tomorrow's weekday.
    const d = nextTeachingDay(clock.ymd)?.day ?? DAYS[0];
    return { day: d, row: teacherDay(teacherId, slots, schedule, d, null).find((r) => r.kind === 'lesson') ?? null };
  }, [teacherId, slots, schedule, today, clock.ymd]);

  const teaching = moment.state === 'lesson' && nowRow?.kind === 'lesson';
  const coverNow = moment.state === 'lesson' && nowRow && nowRow.kind === 'free' ? coverIn(nowRow) : null;
  const pct = Math.round((moment.fraction ?? 0) * 100);
  const withWhom = nowRow ? coTeachers(nowRow.groups, teacherId).map(nameOf) : [];
  const nowEyebrow = moment.state === 'lesson' && moment.period != null
    ? `Now · Period ${moment.period} · ${hhmm(moment.from!)}–${hhmm(moment.to!)}`
    : moment.state === 'break' || moment.state === 'registration'
      ? `Now · ${moment.state === 'break' ? 'Break' : 'Registration'}${moment.to ? ` until ${hhmm(moment.to)}` : ''}`
      : moment.state === 'before' ? 'Before school' : moment.state === 'after' ? 'After school' : 'No teaching today';
  const nowTitle = teaching ? `${title(nowRow!)}${lessonRoom(nowRow!) ? `, ${lessonRoom(nowRow!)}` : ''}`
    : coverNow ? `Cover: ${coverText(coverNow)}${coverNow.room ? `, ${coverNow.room}` : ''}`
    : moment.state === 'lesson' ? 'Free period'
    : moment.state === 'break' ? 'Break' : moment.state === 'registration' ? 'Registration'
    : moment.state === 'before' ? 'School has not started'
    : moment.state === 'after' ? 'Lessons are over for today' : 'No school today';
  const firstLater = later.find((r) => r.kind === 'lesson');
  const nowLine = coverNow ? `For ${coverNow.absentTeacherName} · bell at ${hhmm(moment.to!)}`
    : teaching
    ? [withWhom.length ? `with ${withWhom.join(', ')}` : '', `bell at ${hhmm(moment.to!)}`].filter(Boolean).join(' · ')
    : firstLater ? `Next: ${firstLater.label} ${title(firstLater)} at ${hhmm(firstLater.from)}`
    : tomorrow.row ? `${tomorrow.day} starts with ${tomorrow.row.label} ${title(tomorrow.row)}` : 'No more lessons today';
  const showRing = moment.minutesLeft != null && (moment.state === 'lesson' || moment.state === 'break' || moment.state === 'registration');
  const clockText = `${String(Math.floor(clock.minutes / 60)).padStart(2, '0')}:${String(clock.minutes % 60).padStart(2, '0')}`;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="flex min-w-0 flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-2xl font-bold tracking-tight text-ink dark:text-slate-100">{today ?? 'Weekend'}, {clockText}</p>
          <p className="text-sm text-[var(--sp-muted)]">{today ? `${doneLessons.length} lesson${doneLessons.length === 1 ? '' : 's'} done, ${lessons.length - doneLessons.length} to go` : `${tomorrow.day} is next`}</p>
        </div>

        {doneLessons.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[var(--sp-track)] px-4 py-2.5 text-sm text-[var(--sp-body)]">
            <Check size={16} className="shrink-0 text-[var(--sp-muted)]" />
            <span className="min-w-0 flex-1">Done: {doneLessons.map((r) => `${r.label} ${title(r)}`).join(' · ')}</span>
            <a href="#/lessonRegister" className="text-xs font-semibold text-primary hover:underline dark:text-sky-300">Registers</a>
          </div>
        )}

        <div className={`flex flex-col gap-4 rounded-2xl border-2 bg-[var(--sp-card)] p-5 sm:flex-row sm:items-center ${teaching ? 'border-primary' : 'border-[var(--sp-line)]'}`}>
          {showRing && (
            <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-full"
              style={{ background: `conic-gradient(rgb(47 108 169) 0 ${pct}%, var(--sp-track) ${pct}% 100%)` }}>
              <div className="flex h-[5.5rem] w-[5.5rem] flex-col items-center justify-center rounded-full bg-[var(--sp-card)]">
                <span className="text-3xl font-bold leading-none text-ink dark:text-slate-100">{moment.minutesLeft}</span>
                <span className="text-xs text-[var(--sp-muted)]">min left</span>
              </div>
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-xs font-bold uppercase tracking-wider text-primary dark:text-sky-300">{nowEyebrow}</p>
            <p className="text-2xl font-bold leading-tight tracking-tight text-ink sm:text-3xl dark:text-slate-100">{nowTitle}</p>
            <p className="text-sm text-[var(--sp-body)]">{nowLine}</p>
          </div>
          <div className="flex gap-2 sm:flex-col">
            {teaching && (
              <a href="#/lessonRegister" className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-bold text-white hover:bg-primaryLight dark:text-white">
                <ClipboardCheck size={16} /> Register
              </a>
            )}
            <button type="button" onClick={onOpenWeek} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-lg border-2 border-[var(--sp-line)] px-4 text-sm font-semibold text-[var(--sp-body)] hover:border-[var(--sp-line-2)]">
              <CalendarDays size={16} /> My week
            </button>
          </div>
        </div>

        {later.map((r) => {
          const cov = r.kind === 'free' ? coverIn(r) : null;
          if (cov) {
            return (
              <div key={`${r.label}-${r.time}`}
                className="grid grid-cols-[3.5rem_6.5rem_minmax(0,1fr)] items-center gap-3 rounded-r-xl border border-l-4 border-fuchsia-200 border-l-fuchsia-500 bg-fuchsia-50 px-4 py-3 sm:grid-cols-[3.5rem_6.5rem_minmax(0,1fr)_auto] dark:border-fuchsia-900 dark:bg-fuchsia-950/40">
                <span className="text-xs font-bold text-fuchsia-800 dark:text-fuchsia-200">{r.label}</span>
                <span className="text-xs text-fuchsia-800 dark:text-fuchsia-200">{time(r.time)}</span>
                <span className="truncate text-sm font-semibold text-fuchsia-900 dark:text-fuchsia-100">Cover: {coverText(cov)}</span>
                <span className="col-start-3 text-xs text-fuchsia-800 sm:col-start-auto dark:text-fuchsia-200">{[cov.room, `for ${cov.absentTeacherName}`].filter(Boolean).join(' · ')}</span>
              </div>
            );
          }
          return (
          <div key={`${r.label}-${r.time}`}
            className={`grid grid-cols-[3.5rem_6.5rem_minmax(0,1fr)] items-center gap-3 rounded-xl border border-[var(--sp-line)] px-4 py-3 sm:grid-cols-[3.5rem_6.5rem_minmax(0,1fr)_auto] ${
              r.kind === 'lesson' ? 'bg-[var(--sp-card)]' : 'bg-[var(--sp-paper)]'}`}>
            <span className="text-xs font-bold text-[var(--sp-muted)]">{r.label}</span>
            <span className="text-xs text-[var(--sp-muted)]">{time(r.time)}</span>
            <span className={`truncate text-sm font-semibold ${r.kind === 'lesson' ? 'text-ink dark:text-slate-100' : 'text-[var(--sp-muted)]'}`}>
              {r.kind === 'lesson' ? title(r) : r.kind === 'free' ? 'Free period' : r.label}
            </span>
            {r.kind === 'lesson' && <span className="col-start-3 text-xs text-[var(--sp-body)] sm:col-start-auto">{lessonRoom(r)}</span>}
          </div>
          );
        })}
        {today && !later.length && moment.state !== 'lesson' && <p className="px-1 text-sm text-[var(--sp-muted)]">Nothing more today.</p>}
      </div>

      <div className="flex flex-col gap-3">
        <SoftCard pad="p-4">
          <p className="mb-3 text-lg font-bold tracking-tight text-ink dark:text-slate-100">Today at a glance</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              [String(lessons.length), `lesson${lessons.length === 1 ? '' : 's'}`],
              [String(freesLeft.length), 'free periods left'],
              [String(duties.length), `exam dut${duties.length === 1 ? 'y' : 'ies'}`],
              [lastBell || '—', 'last bell'],
            ].map(([v, l]) => (
              <div key={l} className="rounded-lg bg-[var(--sp-paper)] p-3">
                <p className="text-2xl font-bold text-ink dark:text-slate-100">{v}</p>
                <p className="text-xs text-[var(--sp-body)]">{l}</p>
              </div>
            ))}
          </div>
        </SoftCard>
        {covers.map((c) => (
          <div key={c.id} className="rounded-r-xl border border-l-4 border-fuchsia-200 border-l-fuchsia-500 bg-fuchsia-50 p-4 dark:border-fuchsia-900 dark:bg-fuchsia-950/40">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-fuchsia-800 dark:text-fuchsia-200"><Users size={13} /> Cover · {c.periodStart === c.periodEnd ? `P${c.periodStart}` : `P${c.periodStart}–${c.periodEnd}`}</p>
            <p className="mt-1 text-sm text-fuchsia-900 dark:text-fuchsia-100"><strong>{coverText(c)}</strong>, {c.from}–{c.to}{c.room ? `, Room ${c.room}` : ''}.</p>
            <p className="text-xs text-fuchsia-800 dark:text-fuchsia-200">For {c.absentTeacherName}</p>
          </div>
        ))}
        {duties.map((d) => (
          <div key={`${d.code}-${d.from}`} className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300"><Clock size={13} /> Exam duty{d.role === 'keyTime' ? ' · key time' : ''}</p>
            <p className="mt-1 text-sm text-amber-900 dark:text-amber-100"><strong>{d.code} {d.subject}</strong>, {d.from}–{d.to}.</p>
            <a href="#/myInvigilation" className="text-xs font-semibold text-amber-900 underline dark:text-amber-200">My exam duties</a>
          </div>
        ))}
        <SoftCard pad="p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[var(--sp-muted)]">{tomorrow.day} starts with</p>
          <p className="mt-1 text-sm text-ink dark:text-slate-100">
            {tomorrow.row ? `${tomorrow.row.label} · ${title(tomorrow.row)} · ${lessonRoom(tomorrow.row) || 'room not set'} · ${hhmm(tomorrow.row.from)}` : 'No lessons'}
          </p>
        </SoftCard>
        <button type="button" onClick={onOpenWeek} className="inline-flex h-11 items-center justify-center rounded-lg border-2 border-[var(--sp-line)] bg-[var(--sp-card)] text-sm font-semibold text-[var(--sp-body)] hover:border-[var(--sp-line-2)]">
          See my whole week
        </button>
      </div>
    </div>
  );
}

/* ============================ the whole school ============================ */

type SchoolTab = 'classes' | 'staff';

export function SchoolNow({
  slots, pointers, schedule, rooms, teachers, nameOf, codeOf, subjectOf, phaseOf,
}: {
  slots: Record<SlotId, TimetableSlot>;
  pointers: Record<PointerKey, TimetablePointer>;
  schedule: ScheduleSettings | null;
  rooms: string[];
  teachers: { id: string }[];
  nameOf: (id: string) => string;
  codeOf: (id: string) => string | undefined;
  subjectOf: SubjectNamer;
  phaseOf: (id: string) => Set<TeacherPhase> | undefined;
}) {
  const now = useNow();
  const clock = schoolClock(now);
  const moment = liveMoment(schedule, clock);
  const [picked, setPicked] = useState<{ day: Day; period: number } | null>(null);
  const view = picked ?? { day: moment.day, period: moment.focus };
  const isNow = !picked && moment.state === 'lesson';
  const isToday = view.day === clock.day;
  const papers = useDutiesOn(isToday ? clock.ymd : null);
  const dayCovers = useCoversOn(isToday ? clock.ymd : null);
  const coverWindows: CoverWindow[] = useMemo(() => dayCovers.flatMap((c) => {
    const a = parseHm(c.from), b = parseHm(c.to);
    return a != null && b != null ? [{ teacherId: c.coverTeacherId, from: a, to: b, label: `${c.classes.join(', ')} ${c.subject}`.trim() }] : [];
  }), [dayCovers]);
  const [tab, setTab] = useState<SchoolTab>('staff');

  const bell = (schedule?.days[view.day] ?? []).filter(isTeachingSlot);
  const viewTime = bell.find((b) => b.period === view.period)?.time ?? '';

  const classNames = useMemo(() => {
    const set = new Set<string>();
    Object.keys(pointers).forEach((k) => { const i = k.lastIndexOf('-'); const j = k.lastIndexOf('-', i - 1); if (j > 0) set.add(k.slice(0, j)); });
    return [...set].sort(compareClassNames);
  }, [pointers]);
  const board = useMemo(() => classBoard(classNames, pointers, slots, view.day, view.period), [classNames, pointers, slots, view.day, view.period]);
  const freeRooms = useMemo(() => freeRoomsAt(rooms, slots, view.day, view.period), [rooms, slots, view.day, view.period]);
  const secondaryIds = useMemo(() => teachers.map((t) => t.id).filter((id) => phaseOf(id)?.has('secondary')), [teachers, phaseOf]);
  const staff = useMemo(() => staffAt(secondaryIds, slots, schedule, view.day, view.period, papers, isToday ? clock.ymd : null, subjectOf, coverWindows),
    [secondaryIds, slots, schedule, view.day, view.period, papers, isToday, clock.ymd, subjectOf, coverWindows]);

  const inLesson = board.filter((b) => b.slot).length;
  const counts = {
    teaching: staff.filter((s) => s.status === 'teaching').length,
    free: staff.filter((s) => s.status === 'free').length,
    examDuty: staff.filter((s) => s.status === 'examDuty').length,
    cover: staff.filter((s) => s.status === 'cover').length,
  };

  return (
    <div className="space-y-4">
      <SoftCard pad="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[var(--sp-muted)]">{isNow ? 'Where everyone is right now' : picked ? 'Where everyone is at' : moment.sentence}</p>
            <p className="font-bold tracking-tight text-2xl text-ink dark:text-slate-100">
              {view.day}, period {view.period} <span className="text-base text-[var(--sp-muted)]">{viewTime && time(viewTime)}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="tt-day">Day</label>
            <select id="tt-day" value={view.day} className={glassFieldClass('!w-auto')}
              onChange={(e) => {
                const day = e.target.value as Day;
                const ps = (schedule?.days[day] ?? []).filter(isTeachingSlot);
                setPicked({ day, period: ps.some((p) => p.period === view.period) ? view.period : (ps[0]?.period ?? 1) });
              }}>
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="flex flex-wrap gap-1" role="group" aria-label="Period">
              {bell.map((b) => (
                <button key={b.period} type="button" aria-pressed={b.period === view.period}
                  onClick={() => setPicked({ day: view.day, period: b.period })}
                  className={`h-10 min-w-[2.5rem] rounded-lg px-2 text-sm font-bold transition ${b.period === view.period
                    ? 'bg-ink text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-[var(--sp-track)] text-[var(--sp-body)] hover:bg-[var(--sp-line)]'}`}>
                  {b.period}
                </button>
              ))}
            </div>
            {picked && <button type="button" onClick={() => setPicked(null)} className="h-10 rounded-lg px-3 text-sm font-semibold text-primary hover:underline dark:text-sky-300">Back to now</button>}
          </div>
        </div>
      </SoftCard>

      <StatStrip>
        <StatChip tone="navy" icon={Users} value={counts.teaching} label="teaching" />
        <StatChip tone="amber" icon={Coffee} value={counts.free} label="free (secondary)" />
        <StatChip tone="slate" icon={Clock} value={counts.examDuty + counts.cover} label={counts.cover ? `on exam duty or cover (${counts.cover} cover)` : 'on exam duty'} />
        <StatChip tone="emerald" icon={DoorOpen} value={freeRooms.length} label={`rooms free · ${inLesson} of ${board.length} classes in a lesson`} />
      </StatStrip>

      <Tabs<SchoolTab> value={tab} onChange={setTab} items={[
        { id: 'staff', label: 'Staff', badge: counts.examDuty || undefined },
        { id: 'classes', label: 'Classes' },
      ]} />

      {tab === 'classes' ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <SoftCard pad="p-4">
            <div className="mb-3 flex flex-wrap gap-1.5 text-xs font-semibold">
              {(['sci', 'maths', 'lang', 'hum', 'arts'] as Family[]).map((f) => <span key={f} className={`rounded-md px-2.5 py-1 ${FAMILY[f]}`}>{FAMILY_LABEL[f]}</span>)}
              <span className="ml-auto self-center font-normal text-[var(--sp-muted)]">Secondary · primary is not timetabled yet</span>
            </div>
            {!board.length ? <p className="text-sm text-[var(--sp-muted)]">No class timetables have been published.</p> : (
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4">
                {board.map(({ cls, slot }) => <BoardTile key={cls} cls={cls} slot={slot} subjectOf={subjectOf} />)}
              </div>
            )}
          </SoftCard>
          <div className="flex min-w-0 flex-col gap-4">
            <SoftCard pad="p-4">
              <p className="mb-2 flex items-baseline justify-between"><span className="font-bold tracking-tight text-lg text-ink dark:text-slate-100">Free rooms</span><span className="text-xs text-[var(--sp-muted)]">{freeRooms.length} of {rooms.filter((r) => !r.includes('|')).length}</span></p>
              {freeRooms.length ? (
                <div className="flex flex-wrap gap-1.5">{freeRooms.map((r) => <span key={r} className="rounded-md bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-900 dark:bg-sky-950/40 dark:text-sky-200">{r}</span>)}</div>
              ) : <p className="text-sm text-[var(--sp-muted)]">Every room is in use.</p>}
            </SoftCard>
            <SoftCard pad="p-4">
              <p className="mb-2 flex items-baseline justify-between"><span className="font-bold tracking-tight text-lg text-ink dark:text-slate-100">Free to cover</span><span className="text-xs text-[var(--sp-muted)]">{counts.free} secondary</span></p>
              <ul className="space-y-1.5">
                {staff.filter((s) => s.status === 'free').slice(0, 8).map((s) => (
                  <li key={s.teacherId} className="flex justify-between gap-2 text-sm"><span className="truncate text-ink dark:text-slate-100">{nameOf(s.teacherId)}</span><span className="shrink-0 text-xs text-[var(--sp-muted)]">{s.next ? s.next.split(' · ')[0] : 'free'}</span></li>
                ))}
              </ul>
              {counts.free > 8 && <button type="button" onClick={() => setTab('staff')} className="mt-2 text-xs font-semibold text-primary hover:underline dark:text-sky-300">All {counts.free} free teachers</button>}
            </SoftCard>
          </div>
        </div>
      ) : (
        <StaffTable rows={staff} counts={counts} nameOf={nameOf} codeOf={codeOf} />
      )}
    </div>
  );
}

function BoardTile({ cls, slot, subjectOf }: { cls: string; slot: TimetableSlot | null; subjectOf: SubjectNamer }) {
  if (!slot) {
    return (
      <div className="flex min-h-[4.5rem] flex-col justify-between rounded-lg border border-dashed border-[var(--sp-line-2)] px-3 py-2.5">
        <span className="font-semibold text-ink dark:text-slate-100">{cls}</span>
        <span className="text-xs text-[var(--sp-muted)]">No lesson</span>
      </div>
    );
  }
  const groups: SlotGroup[] = slotGroups(slot).filter((g) => !g.classes?.length || g.classes.includes(cls));
  const subjects = [...new Set(groups.map((g) => subjectOf(g) ?? '').filter(Boolean))];
  const roomSet = [...new Set(groups.map((g) => g.room).filter(Boolean))];
  const room = roomSet.length === 1 ? roomSet[0] : roomSet.length ? `${roomSet.length} rooms` : '';
  const fam = subjects.length === 1 ? familyOf(subjects[0]) : familyOf(subjects.join(' '));
  return (
    <div className={`flex min-h-[4.5rem] min-w-0 flex-col justify-between gap-1 rounded-lg px-3 py-2.5 ${FAMILY[fam]}`}>
      <span className="flex items-center justify-between gap-2">
        <span className="font-semibold">{cls}</span>
        {room && <span className="whitespace-nowrap rounded bg-white/75 px-1.5 py-0.5 text-[11px] font-bold text-slate-800 dark:bg-black/30 dark:text-slate-100">{room}</span>}
      </span>
      <span className="truncate text-xs font-medium" title={subjects.join(' · ')}>{subjects.join(' · ') || 'Lesson'}</span>
    </div>
  );
}

const STATUS: Record<StaffStatus, { label: string; chip: string }> = {
  teaching: { label: 'Teaching', chip: 'bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200' },
  free: { label: 'Free', chip: 'bg-[var(--sp-track)] text-[var(--sp-body)]' },
  examDuty: { label: 'Exam duty', chip: 'bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-200' },
  cover: { label: 'Cover', chip: 'bg-fuchsia-50 text-fuchsia-900 dark:bg-fuchsia-950/40 dark:text-fuchsia-200' },
};
const DAY_MARK = { lesson: 'bg-sky-300 dark:bg-sky-700', duty: 'bg-violet-400 dark:bg-violet-600', cover: 'bg-fuchsia-500 dark:bg-fuchsia-500', free: 'bg-[var(--sp-track)]' };

function StaffTable({ rows, counts, nameOf, codeOf }: {
  rows: ReturnType<typeof staffAt>;
  counts: Record<StaffStatus, number>;
  nameOf: (id: string) => string;
  codeOf: (id: string) => string | undefined;
}) {
  const [filter, setFilter] = useState<'all' | StaffStatus>('all');
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = rows
    .filter((r) => filter === 'all' || r.status === filter)
    .filter((r) => !needle || `${nameOf(r.teacherId)} ${codeOf(r.teacherId) ?? ''} ${r.where}`.toLowerCase().includes(needle))
    .sort((a, b) => surname(nameOf(a.teacherId)).localeCompare(surname(nameOf(b.teacherId))));
  const chip = (id: 'all' | StaffStatus, label: string, n: number) => (
    <button key={id} type="button" onClick={() => setFilter(id)} aria-pressed={filter === id}
      className={`h-10 rounded-full px-4 text-sm font-semibold transition ${filter === id
        ? 'bg-ink text-white dark:bg-slate-100 dark:text-slate-900' : 'border border-[var(--sp-line)] bg-[var(--sp-card)] text-[var(--sp-body)]'}`}>
      {label} {n}
    </button>
  );
  return (
    <SoftCard pad="p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--sp-line)] p-3">
        {chip('all', 'All', rows.length)}
        {chip('teaching', 'Teaching', counts.teaching)}
        {chip('free', 'Free', counts.free)}
        {counts.examDuty > 0 && chip('examDuty', 'Exam duty', counts.examDuty)}
        {counts.cover > 0 && chip('cover', 'Cover', counts.cover)}
        <label className="relative ml-auto">
          <span className="sr-only">Find a teacher</span>
          <Search size={14} className="absolute left-2.5 top-3 text-[var(--sp-muted)]" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, code or room" className={glassFieldClass('!w-56 pl-8')} />
        </label>
      </div>
      <p className="px-4 pt-2 text-xs text-[var(--sp-muted)]">Secondary staff (primary is not timetabled). Exam duty is read from the invigilation rota and wins over a lesson.</p>
      <ul className="divide-y divide-[var(--sp-line)]">
        {shown.map((r) => (
          <li key={r.teacherId} className="grid grid-cols-1 gap-1 px-4 py-2.5 md:grid-cols-[14rem_7rem_minmax(0,1fr)_minmax(0,0.8fr)_9rem] md:items-center md:gap-3">
            <span className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100 text-[11px] font-bold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{codeOf(r.teacherId) ?? '—'}</span>
              <span className="truncate text-sm font-semibold text-ink dark:text-slate-100">{nameOf(r.teacherId)}</span>
            </span>
            <span className={`justify-self-start rounded-md px-2 py-0.5 text-xs font-bold ${STATUS[r.status].chip}`}>{STATUS[r.status].label}</span>
            <span className="truncate text-sm text-[var(--sp-body)]">{r.where || '—'}</span>
            <span className="truncate text-xs text-[var(--sp-muted)]">{r.next ? `Next: ${r.next}` : 'Nothing else today'}</span>
            <span className="flex gap-0.5" aria-label="Their day">
              {r.day.map((m, i) => <span key={i} className={`h-4 w-3 rounded-sm ${DAY_MARK[m]}`} />)}
            </span>
          </li>
        ))}
        {!shown.length && <li className="px-4 py-6 text-sm text-[var(--sp-muted)]">Nobody matches.</li>}
      </ul>
    </SoftCard>
  );
}

/* ============================ ordering ============================ */

const surname = (name: string) => name.replace(/^(mr|mrs|ms|miss|dr|prof)\.?\s+/i, '').split(/\s+/).pop() ?? name;

/** Form 1A … Form 5C, then AS, then A2, then anything else by name. */
export function compareClassNames(a: string, b: string): number {
  const rank = (s: string) => {
    const f = /^Form\s*(\d)/i.exec(s);
    if (f) return Number(f[1]);
    if (/^AS\b/i.test(s)) return 6;
    if (/^A2\b/i.test(s)) return 7;
    return 8;
  };
  return rank(a) - rank(b) || a.localeCompare(b, undefined, { numeric: true });
}

